import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';

const BudgetContext = createContext();

/**
 * Budget Context Provider (P4 - 50% API Reduction Fix)
 *
 * Provides shared state and caching for budget and forecast data.
 * Reduces redundant API calls by caching data with 60-second TTL.
 *
 * Usage:
 *   import { useBudget } from '../context/BudgetContext';
 *   const { budgets, alerts, loadBudgets, loadAlerts } = useBudget();
 *
 * Features:
 *   - Automatic caching with configurable TTL
 *   - Prevents duplicate concurrent requests
 *   - Invalidation support for mutations
 *   - Loading and error states
 */
export function BudgetProvider({ children }) {
  const API_BASE = process.env.REACT_APP_API_URL || '';

  // State
  const [budgets, setBudgets] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [forecasts, setForecasts] = useState(null);

  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [forecastsLoading, setForecastsLoading] = useState(false);

  const [budgetsError, setBudgetsError] = useState(null);
  const [alertsError, setAlertsError] = useState(null);
  const [forecastsError, setForecastsError] = useState(null);

  // Cache timestamps
  const budgetsCacheTime = useRef(null);
  const alertsCacheTime = useRef(null);
  const forecastsCacheTime = useRef(null);

  // In-flight request prevention
  const budgetsRequestInFlight = useRef(false);
  const alertsRequestInFlight = useRef(false);
  const forecastsRequestInFlight = useRef(false);

  // Cache TTL (60 seconds)
  const CACHE_TTL = 60000;

  /**
   * Check if cached data is still valid
   */
  const isCacheValid = (cacheTime) => {
    if (!cacheTime) return false;
    return Date.now() - cacheTime < CACHE_TTL;
  };

  /**
   * Get authentication token
   */
  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  /**
   * Load budgets from API (with caching)
   * @param {boolean} force - Force refresh even if cache is valid
   */
  const loadBudgets = useCallback(async (force = false) => {
    try {
      // Return cached data if valid and not forcing refresh
      if (!force && budgets && isCacheValid(budgetsCacheTime.current)) {
        return budgets;
      }

      // Prevent duplicate concurrent requests
      if (budgetsRequestInFlight.current) {
        return budgets; // Return stale data if request in flight
      }

      budgetsRequestInFlight.current = true;
      setBudgetsLoading(true);
      setBudgetsError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/budget`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load budgets: ${response.statusText}`);
      }

      const data = await response.json();

      setBudgets(data.budgets || []);
      budgetsCacheTime.current = Date.now();

      return data.budgets || [];
    } catch (error) {
      console.error('[BudgetContext] Error loading budgets:', error);
      setBudgetsError(error.message);
      throw error;
    } finally {
      setBudgetsLoading(false);
      budgetsRequestInFlight.current = false;
    }
  }, [budgets, API_BASE]);

  /**
   * Load alerts from API (with caching)
   * @param {boolean} force - Force refresh even if cache is valid
   */
  const loadAlerts = useCallback(async (force = false) => {
    try {
      // Return cached data if valid and not forcing refresh
      if (!force && alerts && isCacheValid(alertsCacheTime.current)) {
        return alerts;
      }

      // Prevent duplicate concurrent requests
      if (alertsRequestInFlight.current) {
        return alerts; // Return stale data if request in flight
      }

      alertsRequestInFlight.current = true;
      setAlertsLoading(true);
      setAlertsError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/budget/alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load alerts: ${response.statusText}`);
      }

      const data = await response.json();

      setAlerts(data.alerts || []);
      alertsCacheTime.current = Date.now();

      return data.alerts || [];
    } catch (error) {
      console.error('[BudgetContext] Error loading alerts:', error);
      setAlertsError(error.message);
      throw error;
    } finally {
      setAlertsLoading(false);
      alertsRequestInFlight.current = false;
    }
  }, [alerts, API_BASE]);

  /**
   * Load forecasts from API (with caching)
   * @param {number} daysAhead - Number of days to forecast (default 30)
   * @param {boolean} force - Force refresh even if cache is valid
   */
  const loadForecasts = useCallback(async (daysAhead = 30, force = false) => {
    try {
      // Return cached data if valid and not forcing refresh
      if (!force && forecasts && isCacheValid(forecastsCacheTime.current)) {
        return forecasts;
      }

      // Prevent duplicate concurrent requests
      if (forecastsRequestInFlight.current) {
        return forecasts; // Return stale data if request in flight
      }

      forecastsRequestInFlight.current = true;
      setForecastsLoading(true);
      setForecastsError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/forecast?days_ahead=${daysAhead}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load forecasts: ${response.statusText}`);
      }

      const data = await response.json();

      setForecasts(data.forecasts || []);
      forecastsCacheTime.current = Date.now();

      return data.forecasts || [];
    } catch (error) {
      console.error('[BudgetContext] Error loading forecasts:', error);
      setForecastsError(error.message);
      throw error;
    } finally {
      setForecastsLoading(false);
      forecastsRequestInFlight.current = false;
    }
  }, [forecasts, API_BASE]);

  /**
   * Create or update a budget (invalidates cache)
   * @param {string} category - Budget category
   * @param {number} monthlyLimit - Monthly spending limit
   * @param {number} alertThreshold - Alert threshold (0-1)
   */
  const saveBudget = useCallback(async (category, monthlyLimit, alertThreshold) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/budget`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category,
          monthly_limit: monthlyLimit,
          alert_threshold: alertThreshold
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save budget');
      }

      const data = await response.json();

      // Invalidate caches to force refresh
      budgetsCacheTime.current = null;
      alertsCacheTime.current = null;

      // Trigger immediate refresh
      await Promise.all([
        loadBudgets(true),
        loadAlerts(true)
      ]);

      return data.budget;
    } catch (error) {
      console.error('[BudgetContext] Error saving budget:', error);
      throw error;
    }
  }, [API_BASE, loadBudgets, loadAlerts]);

  /**
   * Delete a budget (invalidates cache)
   * @param {string} category - Budget category to delete
   */
  const deleteBudget = useCallback(async (category) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/budget/${encodeURIComponent(category)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete budget');
      }

      // Invalidate caches to force refresh
      budgetsCacheTime.current = null;
      alertsCacheTime.current = null;

      // Trigger immediate refresh
      await Promise.all([
        loadBudgets(true),
        loadAlerts(true)
      ]);

      return true;
    } catch (error) {
      console.error('[BudgetContext] Error deleting budget:', error);
      throw error;
    }
  }, [API_BASE, loadBudgets, loadAlerts]);

  /**
   * Generate new forecasts (invalidates cache)
   * @param {number} daysAhead - Number of days to forecast
   */
  const generateForecasts = useCallback(async (daysAhead = 30) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/my-ledger/forecast/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days_ahead: daysAhead })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate forecasts');
      }

      const data = await response.json();

      // Invalidate forecast cache to force refresh
      forecastsCacheTime.current = null;

      // Trigger immediate refresh
      await loadForecasts(daysAhead, true);

      return data;
    } catch (error) {
      console.error('[BudgetContext] Error generating forecasts:', error);
      throw error;
    }
  }, [API_BASE, loadForecasts]);

  /**
   * Manually invalidate all caches
   */
  const invalidateCache = useCallback(() => {
    budgetsCacheTime.current = null;
    alertsCacheTime.current = null;
    forecastsCacheTime.current = null;
  }, []);

  const value = {
    // Data
    budgets,
    alerts,
    forecasts,

    // Loading states
    budgetsLoading,
    alertsLoading,
    forecastsLoading,

    // Error states
    budgetsError,
    alertsError,
    forecastsError,

    // Methods
    loadBudgets,
    loadAlerts,
    loadForecasts,
    saveBudget,
    deleteBudget,
    generateForecasts,
    invalidateCache
  };

  return (
    <BudgetContext.Provider value={value}>
      {children}
    </BudgetContext.Provider>
  );
}

/**
 * Hook to use budget context
 * @returns {object} Budget context value
 */
export function useBudget() {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
}
